import { useCallback, useEffect, useState } from 'react';
import type { OfflinePackId } from '../../domain/tools/types';
import {
  installAllPacks,
  installPack,
  listInstalledPacks,
  loadPackManifest,
  onPackStateChange,
  removePack,
  type InstalledPack,
  type OfflinePackManifest,
  type PackProgress,
} from './packManager';

interface PackHookState {
  readonly manifest?: OfflinePackManifest;
  readonly installed: ReadonlyMap<OfflinePackId, InstalledPack>;
  readonly loading: boolean;
  readonly error?: Error;
  readonly progress?: PackProgress;
}

export function useOfflinePacks() {
  const [state, setState] = useState<PackHookState>({ installed: new Map(), loading: true });

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const [manifest, installed] = await Promise.all([loadPackManifest(), listInstalledPacks()]);
      setState((current) => ({
        ...current,
        manifest,
        installed: new Map(installed.map((pack) => [pack.id, pack])),
        loading: false,
        error: undefined,
      }));
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error instanceof Error ? error : new Error(String(error)) }));
    }
  }, []);

  useEffect(() => {
    void refresh();
    return onPackStateChange(() => void refresh());
  }, [refresh]);

  const withProgress = useCallback(async (operation: () => Promise<unknown>): Promise<void> => {
    setState((current) => ({ ...current, loading: true, error: undefined, progress: undefined }));
    try {
      await operation();
      await refresh();
      setState((current) => ({ ...current, loading: false, progress: undefined }));
    } catch (error) {
      setState((current) => ({
        ...current,
        loading: false,
        progress: undefined,
        error: error instanceof Error ? error : new Error(String(error)),
      }));
      throw error;
    }
  }, [refresh]);

  const install = useCallback((id: OfflinePackId, signal?: AbortSignal) => withProgress(
    () => installPack(id, (progress) => setState((current) => ({ ...current, progress })), signal),
  ), [withProgress]);

  const installEverything = useCallback((signal?: AbortSignal) => withProgress(
    () => installAllPacks((progress) => setState((current) => ({ ...current, progress })), signal),
  ), [withProgress]);

  const remove = useCallback((id: OfflinePackId) => withProgress(() => removePack(id)), [withProgress]);

  return { ...state, install, installEverything, remove, refresh };
}

export function useStorageEstimate(): StorageEstimate & { readonly loading: boolean } {
  const [estimate, setEstimate] = useState<StorageEstimate & { loading: boolean }>({ loading: true });

  useEffect(() => {
    let active = true;
    const update = async (): Promise<void> => {
      const next = await navigator.storage?.estimate?.();
      if (active) setEstimate({ ...next, loading: false });
    };
    void update();
    const interval = window.setInterval(() => void update(), 15_000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, []);

  return estimate;
}
